import React, { useEffect, useState, useRef  } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Paper, Text, Container, Grid, Button, Image, Badge, Table, Input, Modal, Loader, TextInput } from '@mantine/core';
import { getUserFullDetails } from '../Redux/user/slice';
import { addFieldAPI, getFieldAPI } from '../Redux/user/api';
import { IconEdit, IconSave, IconTrash } from '@tabler/icons-react';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastProperties } from '../utils/constants/toastProperties';
import { BASE_URL } from '../Redux/apiConfig';
import { TailSpin } from 'react-loader-spinner';
import { IntlProvider, FormattedMessage, useIntl } from 'react-intl';

const RemoveFieldConfirmationModal = ({ fieldName, onConfirm, onCancel, opened }) => {
  return (
    <Modal
      title={`Remove Field "${fieldName}"`}
      size="sm"
      opened={opened}
      onClose={onCancel}
      shadow="xs"
    >
      <Text  style={{ margin:'10px' }}>
        <FormattedMessage id="myfarm.text1" />{fieldName}<FormattedMessage id="myfarm.text2b" />
      </Text>
      <Button variant="light" color="blue" onClick={onCancel}  style={{ margin:'10px' }}>
        <FormattedMessage id="button.cancel" />
      </Button>
      <Button variant="filled" color="red" onClick={onConfirm}  style={{ margin:'10px' }}>
        <FormattedMessage id="button.confirm" />
      </Button>
    </Modal>
  );
};

export default function MobileMyFarm() {
  const userinfo = JSON.parse(localStorage.getItem('userinfo'));
  const userFullDetails = useSelector((state) => state.user.userFullDetails);
  const dispatch = useDispatch();
  const [lastClickTime, setLastClickTime] = useState(0);

  const [farmFields, setFarmFields] = useState([]);
  const [isLoadingGPS, setIsLoadingGPS] = useState(false);
  const [manualEntry, setManualEntry] = useState(false); // State to track manual entry mode
  const [gmapsEntry, setGmapsEntry] = useState(false);
  const [gmapsLink, setGmapsLink] = useState('');
  const [isResolvingLink, setIsResolvingLink] = useState(false);

  const parseGoogleMapsLink = (url) => {
    // Pattern 1: standard @lat,lng (e.g. /maps/@3.1234,5.6789,17z)
    let match = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    // Pattern 2: ?q=lat,lng or &q=lat,lng
    match = url.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    // Pattern 3: /place/lat,lng/ — produced by goo.gl short-link redirects
    match = url.match(/\/place\/(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    // Pattern 4: !3d{lat}!4d{lng} — Google Maps data encoding
    const lat3d = url.match(/!3d(-?\d+\.?\d*)/);
    const lng4d = url.match(/!4d(-?\d+\.?\d*)/);
    if (lat3d && lng4d) return { latitude: parseFloat(lat3d[1]), longitude: parseFloat(lng4d[1]) };
    // Pattern 5: ll=lat,lng
    match = url.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (match) return { latitude: parseFloat(match[1]), longitude: parseFloat(match[2]) };
    return null;
  };

  const handleSaveGmapsLink = async (fieldIndex) => {
    let urlToParse = gmapsLink.trim();
    if (!urlToParse) {
      toast.error('Please paste a Google Maps link first', { ...ToastProperties });
      return;
    }
    setIsResolvingLink(true);
    if (urlToParse.includes('goo.gl')) {
      try {
        const res = await fetch(`${BASE_URL}/api/resolve-url?url=${encodeURIComponent(urlToParse)}`);
        const data = await res.json();
        if (data.resolvedUrl) urlToParse = data.resolvedUrl;
      } catch {
        setIsResolvingLink(false);
        toast.error('Could not resolve the short link. Please use the full Google Maps URL.', { ...ToastProperties });
        return;
      }
    }
    const parsed = parseGoogleMapsLink(urlToParse);
    setIsResolvingLink(false);
    if (!parsed) {
      toast.error('Could not extract coordinates from this Google Maps link', { ...ToastProperties });
      return;
    }
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].gpsPoints.push({ ...parsed, timestamp: new Date() });
    setFarmFields(updatedFields);
    setGmapsLink('');
    toast.success(`Coordinates extracted: ${parsed.latitude}, ${parsed.longitude}`, { ...ToastProperties });
  };

  useEffect(() => {
    if (!userFullDetails.userId) {
      if (userinfo?.email) {
        dispatch(getUserFullDetails({ email: userinfo.email }));
      }
    } else {
      getFieldAPI(userFullDetails)
        .then((data) => {
          if (Array.isArray(data.result)) {
            setFieldData(data.result);
          } else {
            console.error('Invalid data format:', data);
            setFieldData([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    }
  }, [dispatch, userinfo?.email, userFullDetails.userId]);

  const [showFieldSection, setShowFieldSection] = useState(false);
  const handleAddField = () => {
    // Only add a new field if the field section is not already shown
    if (!showFieldSection && farmFields.length === 0) {
      const field = { name: '', gpsPoints: [], manualLat: '', manualLng: '', region: "", product: "" };
      setGmapsLink('');
      setGmapsEntry(false);
      setManualEntry(false);
      setFarmFields([...farmFields, field]);
    }
    // Toggle the showFieldSection state regardless of whether a field was added or not
    setShowFieldSection(!showFieldSection);
  };

  const handleCollectGPS = async (fieldIndex) => {
    const currentTime = Date.now();
    if (currentTime - lastClickTime < 2000) {
      // If the last click was less than 2 seconds ago, ignore this click
      return;
    }

    setLastClickTime(currentTime);

    setIsLoadingGPS(true);
    await navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const updatedFields = [...farmFields];
        updatedFields[fieldIndex].gpsPoints.push({ latitude, longitude, timestamp: new Date() });
        setFarmFields(updatedFields);
        setIsLoadingGPS(false);
      },
      (err) => {
        console.log(err);
        setIsLoadingGPS(false);
      },{timeout:10000, enableHighAccuracy: true, maximumAge: 0}
    );
  };

  const handleFieldNameChange = (e, fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].name = e.target.value;
    setFarmFields(updatedFields);
  };

  const handleManualLatChange = (e, fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].manualLat = e.target.value;
    setFarmFields(updatedFields);
  };

  const handleManualLngChange = (e, fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].manualLng = e.target.value;
    setFarmFields(updatedFields);
  };

  const handleSaveManualGPS = (fieldIndex) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex].gpsPoints.push({
      latitude: parseFloat(updatedFields[fieldIndex].manualLat),
      longitude: parseFloat(updatedFields[fieldIndex].manualLng),
    });
    setFarmFields(updatedFields);
  };

  const isSaveButtonDisabled = farmFields.some((field) => field.gpsPoints.length < 1);

  const handleSave = (farmFieldsToSave) => {
    if (farmFieldsToSave.length === 0) {
      alert('No farm fields to save.');
      return;
    }

    const hasInvalidFieldName = farmFieldsToSave.some(
      (field) => !field?.name || !field.name.trim()
    );

    if (hasInvalidFieldName) {
      showToast('error', 'Field name is required');
      return;
    }

    const hasInvalidGpsPoints = farmFieldsToSave.some(
      (field) => !Array.isArray(field.gpsPoints) || field.gpsPoints.length < 1
    );

    if (hasInvalidGpsPoints) {
      showToast('error', 'At least 1 GPS point is required');
      return;
    }

    const dataToSave = {
      userId: userinfo.userId,
      userAccountId: userinfo.accountId,
      farmFields: [...fieldData, ...farmFieldsToSave],
    };

    addFieldAPI(dataToSave)
      .then((response) => {
        if (response.status === "Success") {
          showToast('success', 'Field saved successfully');
          setFarmFields([]);
          setShowFieldSection(false);
          // After saving, fetch and update the field data
          getFieldAPI(userFullDetails)
            .then((data) => {
              if (Array.isArray(data.result)) {
                setFieldData(data.result);
              } else {
                console.error('Invalid data format:', data);
                setFieldData([]);
              }
            })
            .catch((error) => {
              console.error('Error fetching data:', error);
            });
        } else {
         showToast('error', 'Failed to save data');
        }
      })
      .catch((error) => {
        console.error('Error saving data:', error);
        showToast('error', 'An error occurred while saving data');
      });
  };

  const [fieldData, setFieldData] = useState([]);

  // Initialize editedFields with the existing fieldData
  const [editedFields, setEditedFields] = useState([]);

  const [isEditing, setIsEditing] = useState(false); // State to track editing mode
  const [editingField, setEditingField] = useState(null);
  const [expandedFields, setExpandedFields] = useState({});

  const handleEditField = (fieldName) => {
    setEditingField(fieldName);
    setIsEditing(true);

    // Clone the original farmFields for editing
    const clonedFields = JSON.parse(JSON.stringify(fieldData));
    setEditedFields(clonedFields);

  };

  const handleSaveField = (fieldName) => {
    setIsEditing(false);

    const editedField = editedFields[fieldName];
    const originalField = fieldData[fieldName];

    // Check if the name or gpsPoints are different
    const isNameModified = editedField.name !== originalField.name;
    const isGpsPointsModified = !arraysAreEqual(editedField.gpsPoints, originalField.gpsPoints);

    // If either the name or gpsPoints is modified, call the handleSave method
    if (isNameModified || isGpsPointsModified) {
      const dataToSave = {
        userId: userinfo.userId,
        userAccountId: userinfo.accountId,
        farmFields: editedFields,
      };
      addFieldAPI(dataToSave)
        .then((response) => {
          if (response.status === "Success") {
            showToast('success', 'Field updated successfully');
            // After saving, fetch and update the field data
            getFieldAPI(userFullDetails)
              .then((data) => {
                if (Array.isArray(data.result)) {
                  setFieldData(data.result);
                } else {
                  console.error('Invalid data format:', data);
                  setFieldData([]);
                }
              })
              .catch((error) => {
                console.error('Error fetching data:', error);
              });
          } else {
            showToast('error', 'Failed to update field');
          }
        })
        .catch((error) => {
          console.error('Error updating field:', error);
          showToast('error', 'An error occurred while saving data');
        });
    }

    // Update the original farmFields with edited data
    setFieldData(editedFields);
  };

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [fieldToRemove, setFieldToRemove] = useState(null);

  const handleRemoveField = (index) => {
    const fieldToRemove = fieldData[index].name;
    // Open the confirmation modal
    setIsConfirmationModalOpen(true);
    setFieldToRemove(fieldToRemove);
  };

  const handleConfirmRemove = () => {
    // Close the confirmation modal when confirmed
    setIsConfirmationModalOpen(false);

    // Your removal logic here
    const updatedEditedFields = fieldData.filter((field) => field.name !== fieldToRemove);
    setEditedFields(updatedEditedFields);

    // Upload the updated data without the removed field
    const dataToSave = {
      userId: userinfo.userId,
      userAccountId: userinfo.accountId,
      farmFields: updatedEditedFields,
    };

    addFieldAPI(dataToSave)
      .then((response) => {
        if (response.status === "Success") {
          showToast('success', 'Field removed successfully');
          // After removing the field, fetch and update the field data
          getFieldAPI(userFullDetails)
            .then((data) => {
              if (Array.isArray(data.result)) {
                setFieldData(data.result);
              } else {
                console.error('Invalid data format:', data);
                setFieldData([]);
              }
            })
            .catch((error) => {
              console.error('Error fetching data:', error);
            });
        } else {
          showToast('error', 'Failed to remove field');
        }
      })
      .catch((error) => {
        console.error('Error removing field:', error);
        showToast('error','An error occurred while removing the field');
      });
  };

  const handleCancelRemove = () => {
    // Your cancel logic here
    // Close the confirmation modal when canceled
    setIsConfirmationModalOpen(false);
  };

  // Helper function to check if two arrays are equal
  function arraysAreEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
      return false;
    }

    for (let i = 0; i < arr1.length; i++) {
      if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
        return false;
      }
    }

    return true;
  }

  const handleEditValues = (e, fieldName, rowIndex, columnName) => {
    if (isEditing) {
      const updatedFields = [...editedFields];
      const fieldIndex = parseInt(fieldName, 10); // base 10
      updatedFields[fieldIndex]["gpsPoints"][rowIndex][columnName] = e.target.value;
      setEditedFields(updatedFields);
    }
  };

  const handleEditName = (e, fieldName, key) => {
    if (isEditing) {
      const updatedFields = [...editedFields];
      const fieldIndex = parseInt(fieldName, 10); // base 10
      updatedFields[fieldIndex][key] = e.target.value;
      setEditedFields(updatedFields);
    }
  };

  const handleFieldChange = (e, fieldIndex, key) => {
    const updatedFields = [...farmFields];
    updatedFields[fieldIndex][key] = e.target.value;
    setFarmFields(updatedFields);
  }; 

  const showToast = (type, message) => {

    // Use the toast function to display the appropriate toast message
    if (type === 'success') {
      toast.success(message, {
        ...ToastProperties
      });
    } else if (type === 'error') {
      toast.error(message, {
        ...ToastProperties
      });
    }
  };

  const intl = useIntl();

  if (!userinfo) return null;

  const mapurl = `https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/userSpace/${userinfo.accountId}/farm_fields_map.html`;

  return (
    <Container size="md" style={{ display: 'center', adding: '10px' }}>
      <Paper padding="md" shadow="xs" style={{ display: 'flex', flexDirection: 'column', height: '300px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <iframe 
          src={mapurl}
          style={{ width: '95%', height: '95%', border: 'none' }} 
          title="Embedded HTML"
        ></iframe>
      </div>
      </Paper>
      <Paper padding="md" shadow="xs" style={{ marginTop: '20px', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Image
            src="https://vertoledgerstorage.blob.core.windows.net/sqldbledgerdigests/images/farm.png"
            alt="User Profile Image"
            style={{ width: '200px', height: 'auto' }}
          />
        </div>
        {fieldData.length === 0 && (
          <Text size="sm" color="dimmed" style={{ textAlign: 'center', padding: '16px' }}>
            <FormattedMessage id="myfarm.no.fields" defaultMessage="No fields yet. Add your first field below." />
          </Text>
        )}
        {Object.keys(fieldData).map((fieldName) => {
          const field = fieldData[fieldName];
          const isExpanded = !!expandedFields[fieldName];
          const isEditingThis = isEditing && editingField === fieldName;
          return (
            <div key={fieldName} style={{ marginBottom: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>

              {/* Tappable header */}
              <div
                onClick={() => !isEditingThis && setExpandedFields(prev => ({ ...prev, [fieldName]: !prev[fieldName] }))}
                style={{ padding: '11px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', backgroundColor: isExpanded ? '#f8fffe' : 'white', borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none' }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text weight={600} size="sm" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {field?.name || '—'}
                  </Text>
                  <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                    {field?.product && <Badge size="xs" color="teal" variant="light">{field.product}</Badge>}
                    {field?.region && <Badge size="xs" color="blue" variant="light">{field.region}</Badge>}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                  <Badge size="xs" variant="outline" color="gray">{field?.gpsPoints?.length || 0} pts</Badge>
                  <Text size="xs" color="dimmed" style={{ lineHeight: 1 }}>{isExpanded ? '▲' : '▼'}</Text>
                </div>
              </div>

              {/* Expanded body */}
              {isExpanded && (
                <div style={{ padding: '12px 14px' }}>
                  {isEditingThis ? (
                    <>
                      <TextInput
                        label={intl.formatMessage({ id: "myfarm.field.name" })}
                        value={editedFields[fieldName]?.name || ''}
                        onChange={(e) => handleEditName(e, fieldName, 'name')}
                        size="xs"
                        mb="xs"
                      />
                      <TextInput
                        label={intl.formatMessage({ id: "myfarm.field.product" })}
                        value={editedFields[fieldName]?.product || ''}
                        onChange={(e) => handleEditName(e, fieldName, 'product')}
                        size="xs"
                        mb="xs"
                      />
                      <TextInput
                        label={intl.formatMessage({ id: "myfarm.field.region" })}
                        value={editedFields[fieldName]?.region || ''}
                        onChange={(e) => handleEditName(e, fieldName, 'region')}
                        size="xs"
                        mb="sm"
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <Button size="xs" variant="subtle" color="gray" onClick={() => { setIsEditing(false); setEditingField(null); }}>
                          <FormattedMessage id="button.cancel" />
                        </Button>
                        <Button size="xs" color="teal" onClick={() => handleSaveField(fieldName)}>
                          <FormattedMessage id="button.save" />
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div style={{ display: 'flex', gap: '14px', marginBottom: '10px' }}>
                      <IconEdit size={18} style={{ cursor: 'pointer', color: '#3b82f6' }} onClick={() => handleEditField(fieldName)} />
                      <IconTrash size={18} style={{ cursor: 'pointer', color: '#ef4444' }} onClick={() => handleRemoveField(parseInt(fieldName))} />
                    </div>
                  )}

                  {/* Compact GPS table — 4 decimal places, no timestamp */}
                  {field?.gpsPoints?.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <Table size="xs" striped withBorder withColumnBorders style={{ fontSize: '12px', width: '100%' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '28px', padding: '4px 6px', textAlign: 'center' }}>#</th>
                            <th style={{ padding: '4px 6px' }}><FormattedMessage id="myfarm.field.latitude" defaultMessage="Lat" /></th>
                            <th style={{ padding: '4px 6px' }}><FormattedMessage id="myfarm.field.longitude" defaultMessage="Long" /></th>
                          </tr>
                        </thead>
                        <tbody>
                          {field.gpsPoints.map((point, rowIndex) => (
                            <tr key={rowIndex}>
                              <td style={{ padding: '4px 6px', textAlign: 'center', color: '#6b7280' }}>{rowIndex + 1}</td>
                              <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>
                                {isEditingThis ? (
                                  <Input
                                    size="xs"
                                    value={editedFields[fieldName]?.gpsPoints[rowIndex]?.latitude || ''}
                                    onChange={(e) => handleEditValues(e, fieldName, rowIndex, 'latitude')}
                                  />
                                ) : (
                                  typeof point?.latitude === 'number' ? point.latitude.toFixed(4) : (point?.latitude || '')
                                )}
                              </td>
                              <td style={{ padding: '4px 6px', fontFamily: 'monospace' }}>
                                {isEditingThis ? (
                                  <Input
                                    size="xs"
                                    value={editedFields[fieldName]?.gpsPoints[rowIndex]?.longitude || ''}
                                    onChange={(e) => handleEditValues(e, fieldName, rowIndex, 'longitude')}
                                  />
                                ) : (
                                  typeof point?.longitude === 'number' ? point.longitude.toFixed(4) : (point?.longitude || '')
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

      </Paper>
      <Paper padding="md" shadow="xs" style={{ marginTop: '20px' }}>
        <div style={{ padding: '16px', paddingBottom: farmFields.length > 0 ? '80px' : '16px' }}>
          {!showFieldSection && (
            <Button color="teal" fullWidth onClick={handleAddField}>
              <FormattedMessage id="button.add.farm" />
            </Button>
          )}
          {showFieldSection && (
            <div style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
              {/* Header */}
              <div style={{ backgroundColor: '#0d9488', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text color="white" weight={600} size="sm">
                  <FormattedMessage id="myfarm.add.field" defaultMessage="Add New Field" />
                </Text>
                <button
                  onClick={() => { setShowFieldSection(false); }}
                  style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '2px 4px' }}
                  aria-label="Close"
                >×</button>
              </div>

              {farmFields.map((field, index) => (
                <div key={index} style={{ padding: '16px' }}>

                  {/* Step 1 — Field Details */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#0d9488', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>1</div>
                    <Text weight={600} size="sm">Field Details</Text>
                  </div>

                  <TextInput
                    label={intl.formatMessage({ id:"myfarm.field.name" })}
                    placeholder={intl.formatMessage({ id:"myfarm.field.name" })}
                    value={field.name}
                    onChange={(e) => handleFieldChange(e, index, 'name')}
                    required
                    size="sm"
                    mb="sm"
                  />
                  <TextInput
                    label={intl.formatMessage({ id:"myfarm.field.product" })}
                    placeholder={intl.formatMessage({ id:"myfarm.field.product" })}
                    value={field.product}
                    onChange={(e) => handleFieldChange(e, index, 'product')}
                    size="sm"
                    mb="sm"
                  />
                  <TextInput
                    label={intl.formatMessage({ id:"myfarm.field.region" })}
                    placeholder={intl.formatMessage({ id:"myfarm.field.region" })}
                    value={field.region}
                    onChange={(e) => handleFieldChange(e, index, 'region')}
                    size="sm"
                    mb="md"
                  />

                  <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />

                  {/* Step 2 — GPS Location */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', backgroundColor: '#0d9488', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>2</div>
                    <Text weight={600} size="sm">GPS Location</Text>
                    {field.gpsPoints.length > 0 && (
                      <Badge color="teal" size="xs" variant="filled">{field.gpsPoints.length} added</Badge>
                    )}
                  </div>

                  {/* Method option cards — stacked vertically on mobile */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                    {/* Auto-detect */}
                    <div
                      onClick={() => { setManualEntry(false); setGmapsEntry(false); handleCollectGPS(index); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', border: `2px solid ${!manualEntry && !gmapsEntry ? '#0d9488' : '#e5e7eb'}`, borderRadius: '8px', padding: '12px 14px', cursor: 'pointer', backgroundColor: !manualEntry && !gmapsEntry ? '#f0fdfa' : 'white' }}
                    >
                      <span style={{ fontSize: '20px' }}>📡</span>
                      <div style={{ flex: 1 }}>
                        <Text size="sm" weight={600}>Auto-detect</Text>
                        <Text size="xs" color="dimmed">Use this device's GPS</Text>
                      </div>
                      {isLoadingGPS && <Loader size="xs" color="teal" />}
                    </div>
                    {/* Manual */}
                    <div
                      onClick={() => { setManualEntry(true); setGmapsEntry(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', border: `2px solid ${manualEntry ? '#0d9488' : '#e5e7eb'}`, borderRadius: '8px', padding: '12px 14px', cursor: 'pointer', backgroundColor: manualEntry ? '#f0fdfa' : 'white' }}
                    >
                      <span style={{ fontSize: '20px' }}>✏️</span>
                      <div>
                        <Text size="sm" weight={600}>Manual entry</Text>
                        <Text size="xs" color="dimmed">Type latitude &amp; longitude</Text>
                      </div>
                    </div>
                    {/* Google Maps */}
                    <div
                      onClick={() => { setGmapsEntry(true); setManualEntry(false); }}
                      style={{ display: 'flex', alignItems: 'center', gap: '12px', border: `2px solid ${gmapsEntry ? '#0d9488' : '#e5e7eb'}`, borderRadius: '8px', padding: '12px 14px', cursor: 'pointer', backgroundColor: gmapsEntry ? '#f0fdfa' : 'white' }}
                    >
                      <span style={{ fontSize: '20px' }}>🗺️</span>
                      <div>
                        <Text size="sm" weight={600}>Google Maps link</Text>
                        <Text size="xs" color="dimmed">Paste a maps.app.goo.gl link</Text>
                      </div>
                    </div>
                  </div>

                  {/* Google Maps input */}
                  {gmapsEntry && (
                    <div style={{ marginBottom: '12px' }}>
                      <TextInput
                        label="Google Maps link"
                        placeholder="https://maps.app.goo.gl/... or google.com/maps/..."
                        value={gmapsLink}
                        onChange={(e) => setGmapsLink(e.target.value)}
                        size="sm"
                        mb="xs"
                      />
                      <Button
                        color="teal"
                        fullWidth
                        onClick={() => handleSaveGmapsLink(index)}
                        loading={isResolvingLink}
                        size="sm"
                        mt="xs"
                      >
                        Extract &amp; Add GPS point
                      </Button>
                    </div>
                  )}

                  {/* Manual lat/lng input */}
                  {manualEntry && (
                    <div style={{ marginBottom: '12px' }}>
                      <TextInput
                        label={intl.formatMessage({ id:"myfarm.field.manlat.label" })}
                        placeholder={intl.formatMessage({ id:"myfarm.field.manlat.lorem" })}
                        value={field.manualLat}
                        onChange={(e) => handleManualLatChange(e, index)}
                        size="sm"
                        mb="xs"
                      />
                      <TextInput
                        label={intl.formatMessage({ id:"myfarm.field.manlong.label" })}
                        placeholder={intl.formatMessage({ id:"myfarm.field.manlong.lorem" })}
                        value={field.manualLng}
                        onChange={(e) => handleManualLngChange(e, index)}
                        size="sm"
                        mb="xs"
                      />
                      <Button
                        color="teal"
                        fullWidth
                        onClick={() => handleSaveManualGPS(index)}
                        disabled={!field.manualLat || !field.manualLng}
                        size="sm"
                        mt="xs"
                      >
                        <FormattedMessage id="button.add" defaultMessage="Add GPS point" />
                      </Button>
                    </div>
                  )}

                  {/* Saved GPS points as badges */}
                  {field.gpsPoints.length > 0 && (
                    <div style={{ padding: '10px 12px', backgroundColor: '#f0fdfa', borderRadius: '8px', border: '1px solid #99f6e4' }}>
                      <Text size="xs" color="dimmed" mb="xs" weight={500}>Saved coordinates:</Text>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {field.gpsPoints.map((pt, i) => (
                          <Badge key={i} color="teal" variant="light" size="sm" style={{ fontFamily: 'monospace', fontSize: '10px' }}>
                            #{i + 1} {pt.latitude?.toFixed(5)}, {pt.longitude?.toFixed(5)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              ))}

              {/* Sticky save button */}
              {farmFields.length > 0 && (
                <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000, padding: '12px 16px', backgroundColor: 'white', borderTop: '1px solid #dee2e6', boxShadow: '0 -2px 8px rgba(0,0,0,0.12)' }}>
                  <Button
                    color="teal"
                    fullWidth
                    size="md"
                    onClick={() => handleSave(farmFields)}
                    disabled={isSaveButtonDisabled}
                  >
                    <FormattedMessage id="button.save" />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </Paper>
      <RemoveFieldConfirmationModal
        fieldName
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
        opened={isConfirmationModalOpen} // Pass the opened prop
      />
    </Container>
  );
}
